import { useKeyboardShortcuts } from '@fiverr-private/hooks';
import { getIndexFromTabId } from '../../helpers';
import type { TabContextType, TabContextProps } from '../../types';

type KeyboardNavigationProps = Pick<TabContextProps, 'tabGroupRef' | 'autoSelect'> &
    Pick<TabContextType, 'placement' | 'setSelectedIndex' | 'tabsRef' | 'isFocusedRef'>;

const enum Shift {
    Decr = -1,
    Incr = 1,
}

const shiftIndex = (tabsRef: KeyboardNavigationProps['tabsRef'], index: number, shift: Shift) => {
    const { length } = tabsRef.current;
    let nextIndex = (index + length + shift) % length;
    if (tabsRef.current[nextIndex].isDisabled) {
        nextIndex = shiftIndex(tabsRef, nextIndex, shift);
    }
    return nextIndex;
};

export const useKeyboardNavigation = ({
    placement,
    setSelectedIndex,
    autoSelect,
    tabsRef,
    tabGroupRef,
    isFocusedRef,
}: KeyboardNavigationProps) => {
    const shiftSelectedTab = (e: KeyboardEvent, shift: Shift) => {
        e.preventDefault();
        const focusedIndex = getIndexFromTabId(document.activeElement?.id);
        const nextIndex = shiftIndex(tabsRef, focusedIndex, shift);
        const nextTab = tabGroupRef.current?.querySelector<HTMLElement>(`[role="tab"]:nth-child(${nextIndex + 1})`);
        nextTab?.focus();

        if (autoSelect) {
            selectFocusedTab(e, nextTab);
        }
    };

    const selectFocusedTab = (e: KeyboardEvent, tab?: HTMLElement | null) => {
        e.preventDefault();
        const focusedIndex = getIndexFromTabId(tab?.id);
        setSelectedIndex(focusedIndex);

        if (tab?.dataset.link === 'true') {
            const link = tab?.querySelector<HTMLAnchorElement>('a');
            link?.click();
        }

        tab?.focus();
    };

    const handler = (e: KeyboardEvent) => {
        if (!isFocusedRef.current) return;
        if (placement === 'top' || placement === 'bottom') {
            if (e.code === 'ArrowLeft') shiftSelectedTab(e, Shift.Decr);
            else if (e.code === 'ArrowRight') shiftSelectedTab(e, Shift.Incr);
        } else if (placement === 'left' || placement === 'right') {
            if (e.code === 'ArrowUp') shiftSelectedTab(e, Shift.Decr);
            else if (e.code === 'ArrowDown') shiftSelectedTab(e, Shift.Incr);
        }
        if (!autoSelect && (e.code === 'Space' || e.code === 'Enter')) {
            selectFocusedTab(e, document.activeElement as HTMLElement | null);
        }
    };

    useKeyboardShortcuts([{ trigger: '*', handler }]);
};
